/**
 * The incantation word bank.
 *
 * A chant still accepts anything typed into it — this is a shortcut, not a
 * grammar. It exists for two reasons that happen to have the same fix. Writing
 * a good English incantation under a craft timer is hard for a player who does
 * not think in English; and speech recognition is a dictionary model, so an
 * invented word comes back different every time it is spoken and the player
 * blames their own pronunciation. Both problems go away if the chant is
 * assembled from real words the recogniser already knows.
 *
 * Tokens the recogniser is less reliable on are marked rather than hidden. They
 * are the good-sounding Latin, and a player who wants them should be allowed to
 * take the risk knowingly.
 */
import { useMemo, useState } from 'react';
import {
  LEXICON_CATEGORIES,
  LEXICON_CATEGORY_LABELS,
  SPELL_LEXICON,
  lexiconByCategory,
  type LexiconCategory,
} from '@spell/shared-types';

export interface LexiconPickerProps {
  /** Characters still available in the chant field, including the space. */
  remaining: number;
  onAppend: (word: string) => void;
}

export function LexiconPicker({ remaining, onAppend }: LexiconPickerProps) {
  const [category, setCategory] = useState<LexiconCategory>('element');
  const [query, setQuery] = useState('');

  /**
   * A search spans the whole lexicon, not the open tab: somebody typing "불"
   * wants every fiery word there is, and having to guess which of eight tabs
   * hides it is the problem the box exists to remove. The Korean gloss is
   * searched too, which is the only way in for a player who does not already
   * know the English word they are looking for.
   */
  const tokens = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return lexiconByCategory(category);
    return SPELL_LEXICON.filter(
      (token) => token.word.toLowerCase().includes(q) || token.ko.includes(q),
    );
  }, [category, query]);

  const searching = query.trim().length > 0;

  return (
    <div className="lexicon">
      <div className="lexicon__tabs" role="tablist" data-searching={searching ? 'true' : undefined}>
        {LEXICON_CATEGORIES.map((c) => (
          <button
            key={c}
            type="button"
            role="tab"
            aria-selected={!searching && c === category}
            className={`lexicon__tab ${!searching && c === category ? 'lexicon__tab--active' : ''}`}
            onClick={() => {
              setQuery('');
              setCategory(c);
            }}
          >
            {LEXICON_CATEGORY_LABELS[c]}
          </button>
        ))}
      </div>

      <div className="lexicon__search">
        <input
          className="field"
          data-testid="lexicon-search"
          value={query}
          maxLength={24}
          placeholder="단어 찾기 (영어 또는 뜻) — 예: 불, DOOM"
          onChange={(e) => setQuery(e.target.value)}
        />
        <span className="hint">
          {searching ? `${tokens.length}개 검색됨` : `${tokens.length}개 · 전체 ${SPELL_LEXICON.length}개`}
        </span>
      </div>

      <div className="lexicon__grid" data-testid="lexicon-grid">
        {tokens.length === 0 ? (
          <div className="hint">그런 단어는 없습니다. 영창 칸에 직접 적어도 됩니다.</div>
        ) : null}
        {tokens.map((token) => {
          const tooLong = token.word.length + 1 > remaining;
          return (
            <button
              key={token.word}
              type="button"
              className="lex-token"
              disabled={tooLong}
              data-risk={token.common ? undefined : 'true'}
              title={
                tooLong
                  ? '영창 길이 제한(48자)에 걸립니다'
                  : token.common
                    ? undefined
                    : '사전에 없는 라틴어라 음성 인식이 흔들릴 수 있습니다'
              }
              onClick={() => onAppend(token.word)}
            >
              <span className="lex-token__word">
                {token.word}
                {token.common ? null : (
                  <span className="lex-token__risk" aria-label="인식 불안정"> △</span>
                )}
              </span>
              <span className="lex-token__ko">{token.ko}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
